import { useMemo } from "react";
import { Section } from "views/applications-id-report/components/Section";
import { CombinedIncome } from "../../types/income";
import {
  mapIncomeSourceStatus,
  mapUnverifiedIncomeStatus
} from "../../helpers";
import { IncomeType, ReverifyRequest } from "shared/types";
import { IncomeSourceWarning } from "./components/IncomeSourceWarning";
import { getIncomeJobName } from "../../utils";
import { REVERIFY_FAILURE_REASONS } from "../ReverifySlice/constants";

export const DocumentWarnings = ({
  incomes,
  failedReverifyRequests = []
}: {
  incomes: CombinedIncome[];
  failedReverifyRequests?: ReverifyRequest[];
}) => {
  const incomeSourceItems = useMemo(
    () =>
      incomes
        .map((inc) => mapIncomeSourceStatus(inc))
        .filter(
          (incSource) =>
            incSource.status && incSource.type !== IncomeType.HousingAssistance
        ),
    [incomes]
  );
  const incomeSourceWarnings = incomeSourceItems.filter(
    (incSource) => incSource.status === "Warning"
  );
  const incomeSourceErrors = incomeSourceItems.filter(
    (incSource) => incSource.status === "Rejected"
  );

  const unverifiedIncomeWarnings = incomes
    .filter(
      (income) =>
        income.status === "pending" &&
        Boolean(income.sources?.length) &&
        income.type !== IncomeType.HousingAssistance
    )
    .map(mapUnverifiedIncomeStatus);

  const reverifyFailureWarnings = useMemo(
    () =>
      failedReverifyRequests
        .map((request) => {
          const matchedIncome = incomes.find(
            (income) => income.id === request.income_id
          );
          if (!matchedIncome || !request.failure_reason) return null;
          return {
            requestId: request.id,
            incomeId: matchedIncome.id,
            incomeName: getIncomeJobName(matchedIncome.additional_data),
            description:
              REVERIFY_FAILURE_REASONS[request.failure_reason].subtitle ?? ""
          };
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null),
    [failedReverifyRequests, incomes]
  );

  const warningsCount =
    incomeSourceWarnings.length +
    incomeSourceErrors.length +
    unverifiedIncomeWarnings.length +
    reverifyFailureWarnings.length;
  if (warningsCount === 0) return null;

  return (
    <Section
      sectionTitle="Warnings"
      maxWidth="none"
      sectionClassName="mt-9 mb-10"
    >
      <div className="flex flex-col gap-4">
        {reverifyFailureWarnings.map(
          ({ requestId, incomeId, incomeName, description }) => (
            <IncomeSourceWarning
              key={requestId}
              incomeId={incomeId}
              incomeName={incomeName}
              titleOverride={`Income re-verification failed. Income source: ${incomeName}.`}
              description={description}
              status="Rejected"
            />
          )
        )}
        {unverifiedIncomeWarnings.map(
          ({ incomeName, incomeId, description, status }) => (
            <IncomeSourceWarning
              key={incomeId}
              incomeId={incomeId}
              incomeName={incomeName}
              statusMessage="could not be verified"
              description={description}
              status={status as "Rejected"}
            />
          )
        )}
        {incomeSourceWarnings.map(
          ({ incomeName, incomeId, description, status }) => (
            <IncomeSourceWarning
              key={incomeId}
              incomeId={incomeId}
              incomeName={incomeName}
              statusMessage="has verification warnings"
              description={description}
              status={status as "Warning"}
            />
          )
        )}
        {incomeSourceErrors.map(
          ({ incomeName, incomeId, description, status }) => (
            <IncomeSourceWarning
              key={incomeId}
              incomeId={incomeId}
              incomeName={incomeName}
              statusMessage="has verification warnings"
              description={description}
              status={status as "Rejected"}
            />
          )
        )}
      </div>
    </Section>
  );
};
